import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { LanguageService } from '../../../core/services/language.service';
import { ThemeService } from '../../../core/services/theme.service';
import { environment } from '../../../../environments/environment';

type AlertState = 'idle' | 'sending' | 'sent' | 'error' | 'invalid';

interface FooterLink {
  /** Translation key of the link text. */
  label: string;
  path?: string;
  queryParams?: Record<string, string>;
  href?: string;
  external?: boolean;
}

/**
 * Light footer of the homepage redesign (/home2). Its newsletter slot
 * subscribes to the status alerts, the only mailing list the site offers.
 */
@Component({
  selector: 'app-footer-v2',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslocoDirective],
  templateUrl: './footer-v2.component.html',
  styleUrl: './footer-v2.component.css',
})
export class FooterV2Component {
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly language = inject(LanguageService);
  protected readonly theme = inject(ThemeService);

  protected readonly year = new Date().getFullYear();
  protected readonly alertState = signal<AlertState>('idle');

  protected readonly columns: readonly { key: string; links: readonly FooterLink[] }[] = [
    {
      key: 'products',
      links: [
        { label: 'home2.footer.links.vps', path: '/vps-standard', queryParams: { type: 'kvm' } },
        { label: 'home2.footer.links.performance', path: '/vps-performance', queryParams: { type: 'kvm' } },
        { label: 'home2.footer.links.dedicated', path: '/dedicated' },
        { label: 'home2.footer.links.web', path: '/plesk' },
        { label: 'home2.footer.links.domains', path: '/domains' },
      ],
    },
    {
      key: 'resources',
      links: [
        { label: 'home2.footer.links.status', path: '/status' },
        { label: 'home2.footer.links.panel', href: environment.panelUrl, external: true },
        { label: 'home2.footer.links.antiddos', path: '/antiddos' },
        { label: 'home2.footer.links.datacenter', path: '/datacenter' },
      ],
    },
    {
      key: 'company',
      links: [
        { label: 'home2.footer.links.about', path: '/about' },
        { label: 'home2.footer.links.contact', path: '/contact' },
        { label: 'footer.contactPhone', href: 'tel:+33184754308' },
        { label: 'footer.contactEmail', href: 'mailto:contact@ancelade.com' },
      ],
    },
  ];

  protected subscribe(event: Event, field: HTMLInputElement): void {
    event.preventDefault();
    if (this.alertState() === 'sending') return;
    const email = field.value.trim();
    if (!email || !field.checkValidity()) {
      this.alertState.set('invalid');
      return;
    }
    this.alertState.set('sending');
    this.http
      .post(`${environment.apiUrl}/status/subscriptions`, { email })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          field.value = '';
          this.alertState.set('sent');
        },
        error: () => this.alertState.set('error'),
      });
  }

  protected switchLanguage(code: string): void {
    this.language.switchLang(code);
  }

  protected localizedLink(path: string): string {
    return `/${this.language.currentLang()}${path}`;
  }
}
